import React from 'react';
import PropTypes from 'prop-types';
import { Loc } from 'redux-react-i18n';
import { Swipeable } from 'react-swipeable';
import throttle from 'lodash.throttle';
import Dotdotdot from 'react-dotdotdot';
import Icon from 'components/mediabay-ui/components/Icon';
import Indicator from './Indicator';
import {
  SliderContainer,
  AdSlot,
  SliderSlot,
  SliderDetails,
  LeftNavButton,
  RightNavButton,
  StyledSlider,
  Badge,
  Title,
  Subtitle,
  Description,
  StyledLink,
  StyledExternalLink,
} from './StyledSlider';

class Slider extends React.Component {
  static propTypes = {
    // Actions
    getSlides: PropTypes.func.isRequired,

    // Data
    currentLanguage: PropTypes.string.isRequired,
    data: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.shape({
        eng: PropTypes.string,
        rus: PropTypes.string,
        uzb: PropTypes.string,
      }),
      type: PropTypes.string,
      title: PropTypes.shape({
        eng: PropTypes.string,
        rus: PropTypes.string,
        uzb: PropTypes.string,
      }),
      subtitle: PropTypes.shape({
        eng: PropTypes.string,
        rus: PropTypes.string,
        uzb: PropTypes.string,
      }),
      picture: PropTypes.string,
      tag: PropTypes.string,
    })),
    accentColor: PropTypes.string.isRequired,
    adContainer: PropTypes.node,
    adAvailable: PropTypes.bool,
  }

  static defaultProps = {
    adContainer: null,
    data: PropTypes.arrayOf(PropTypes.shape({
      label: '',
      type: '',
      title: '',
      subtitle: '',
      picture: '',
      tag: '',
    })),
    adAvailable: false,
  }

  constructor(props) {
    super(props);
    this.state = {
      position: 0,
      direction: 'next',
    };

    this.getOrder = this.getOrder.bind(this);
    this.next = this.next.bind(this);
    this.prev = this.prev.bind(this);
    this.doSliding = this.doSliding.bind(this);
    this.timer = null;
  }

  componentDidMount() {
    const { adAvailable } = this.props;

    this.startAutoPlay();
    this.props.getSlides().then((response) => {
      if (response && response.response && adAvailable) response.response.data.unshift({ type: 'ad' });
    });
  }

  componentWillUnmount() {
    clearInterval(this.timer);
  }

  getOrder(itemIndex) {
    const { position } = this.state;
    const numItems = this.props.data.length || 1;

    if (itemIndex - position < 0) {
      return numItems - Math.abs(itemIndex - position);
    }

    return itemIndex - position;
  }

  prev() {
    const { position } = this.state;
    const numItems = this.props.data.length;

    this.doSliding('prev', position === 0 ? numItems - 1 : position - 1);
    this.resetAutoPlay();
  }

  next() {
    const { position } = this.state;
    const numItems = this.props.data.length || 1;

    this.doSliding('next', position === numItems - 1 ? 0 : position + 1);
    this.resetAutoPlay();
  }

  startAutoPlay() {
    this.timer = setInterval(this.next, 7000);
  }

  pauseAutoPlay() {
    if (this.timer) clearInterval(this.timer);
  }

  resetAutoPlay() {
    if (this.timer) clearInterval(this.timer);
    this.timer = setInterval(this.next, 7000);
  }

  doSliding(direction, position) {
    this.setState({
      direction,
      position,
    });
  }

  // eslint-disable-next-line
  swipeHandler = throttle((isNext) => {
    if (isNext) {
      this.next();
    } else {
      this.prev();
    }
  }, 500, { trailing: false })

  render() {
    const {
      currentLanguage,
      data,
      accentColor,
      adContainer,
    } = this.props;
    const { position } = this.state;

    return (
      <Swipeable
        onSwipedLeft={() => this.swipeHandler(true)}
        onSwipedRight={() => this.swipeHandler(false)}
      >
        <StyledSlider ref={(carousel) => { this.carousel = carousel; }}>
          <LeftNavButton icon="arrow-left" color="#747474" onClick={() => this.prev()} />
          <RightNavButton icon="arrow-right" color="#747474" onClick={() => this.next()} />
          <Indicator
            length={data.length}
            position={position}
          />
          <SliderContainer direction={this.state.direction}>
            {
              data.map((item, index) => (
                item.type === 'ad' ? (
                  <AdSlot
                    key="ad"
                    order={this.getOrder(index)}
                  >
                    {adContainer}
                  </AdSlot>
                ) : (
                  <SliderSlot
                    key={item.id}
                    order={this.getOrder(index)}
                    background={item.picture}
                  >
                    <div>
                      <SliderDetails>
                        {
                          <React.Fragment>
                            <Badge
                              color={accentColor}
                              style={{ margin: '10px 0' }}
                            >
                              {item.label && (item.label[currentLanguage] || item.label['ru'])}
                            </Badge>
                            <Title level={2}>
                              {item.title && (item.title[currentLanguage] || item.title['ru'])}
                            </Title>
                            <Subtitle level={3}>
                              {item.subTitle && (item.subTitle[currentLanguage] || item.subTitle['ru'])}
                            </Subtitle>
                            <Description>
                              {
                                <Dotdotdot clamp={3} useNativeClamp>
                                  {
                                    item.description
                                    && (item.description[currentLanguage] || item.description['ru'])
                                  }
                                </Dotdotdot>

                              }
                            </Description>

                            {item.type === 'ad' || item.type === 'tariffs' ? (
                              <StyledExternalLink
                                color={accentColor}
                                href={item.param}
                              >
                                <Loc locKey="details" />
                              </StyledExternalLink>
                            ) : (
                              <StyledLink
                                color={accentColor}
                                to={item.param}
                              >
                                <Icon
                                  icon="play"
                                  width={15}
                                  height={15}
                                  color="#fff"
                                  style={{ padding: '0 5px' }}
                                />

                                <Loc locKey="watch" />
                              </StyledLink>
                            )}
                          </React.Fragment>
                        }
                      </SliderDetails>
                    </div>
                  </SliderSlot>
                )
              ))
            }
          </SliderContainer>
        </StyledSlider>
      </Swipeable>
    );
  }
}

export default Slider;
